/**
 * Point History API Service
 * Handles API communication for point history
 */

import { ApiBase, HttpMethod } from "../../core/api-base";
import { z } from "zod";
import {
    AdjustUserPoints200Response,
    AdjustUserPoints403Response,
    AdjustUserPoints500Response,
    AdjustUserPoints422Response,
    AdjustUserPointsBody,
    GetPointHistories200Response,
    InvalidatePointHistory200Response,
    InvalidatePointHistory403Response,
    InvalidatePointHistory404Response,
    InvalidatePointHistory500Response,
    InvalidatePointHistory422Response,
    InvalidatePointHistoryBody,
} from "../../api/generated/points.zod";

type PointHistoryListResponse = z.infer<typeof GetPointHistories200Response>;
export type PointHistoryItem = PointHistoryListResponse["histories"][number];
type AdjustPointsPayload = z.infer<typeof AdjustUserPointsBody>;
type AdjustPointsResponse =
    | z.infer<typeof AdjustUserPoints200Response>
    | z.infer<typeof AdjustUserPoints403Response>
    | z.infer<typeof AdjustUserPoints500Response>
    | z.infer<typeof AdjustUserPoints422Response>;
type AdjustPointsResult = NonNullable<z.infer<typeof AdjustUserPoints200Response>["data"]> | null;
type InvalidatePointHistoryPayload = z.infer<typeof InvalidatePointHistoryBody>;
type InvalidatePointHistoryResponse =
    | z.infer<typeof InvalidatePointHistory200Response>
    | z.infer<typeof InvalidatePointHistory403Response>
    | z.infer<typeof InvalidatePointHistory404Response>
    | z.infer<typeof InvalidatePointHistory500Response>
    | z.infer<typeof InvalidatePointHistory422Response>;
type InvalidatePointHistoryResult = NonNullable<z.infer<typeof InvalidatePointHistory200Response>["data"]> | null;

interface PointHistoryFetchOptions {
    includeInvalid?: boolean;
}

function getResponseError(data: AdjustPointsResponse | InvalidatePointHistoryResponse): string | undefined {
    return "message" in data ? data.message : undefined;
}

function isAdjustPointsSuccessResponse(
    data: AdjustPointsResponse,
): data is z.infer<typeof AdjustUserPoints200Response> {
    return "success" in data;
}

function isInvalidatePointHistorySuccessResponse(
    data: InvalidatePointHistoryResponse,
): data is z.infer<typeof InvalidatePointHistory200Response> {
    return "success" in data;
}

export class PointHistoryApi extends ApiBase {
    public static async fetchPointHistory(
        userId: number,
        options: PointHistoryFetchOptions = {},
    ): Promise<PointHistoryItem[]> {
        if (!Number.isInteger(userId) || userId <= 0) {
            throw new Error("User ID is required");
        }

        const includeInvalid = options?.includeInvalid === true;
        const query = includeInvalid ? "?include_invalid=1" : "";

        const response = await super.apiRequest(
            `/api/v1/users/${String(userId)}/point-histories${query}`,
            HttpMethod.Get,
            null,
            {
                noSpinner: true,
            },
        );

        if (!response.ok) {
            throw new Error(`Failed to fetch point history: ${response.status}`);
        }

        const data = (await response.json()) as PointHistoryListResponse;

        if (data.success && data.histories) {
            return data.histories;
        }

        throw new Error("Invalid API response");
    }

    public static async adjustPoints(userId: number, payload: AdjustPointsPayload): Promise<AdjustPointsResult> {
        if (!Number.isInteger(userId) || userId <= 0) {
            throw new Error("User ID is required");
        }

        const response = await super.apiRequest(
            `/api/v1/users/${String(userId)}/point-adjustments`,
            HttpMethod.Post,
            payload,
            {
                noSpinner: true,
            },
        );
        const data = (await response.json().catch(() => ({}))) as AdjustPointsResponse;

        if (!response.ok || !isAdjustPointsSuccessResponse(data) || !data.success) {
            throw new Error(getResponseError(data) || "Failed to adjust points");
        }

        return data.data || null;
    }

    public static async invalidatePointHistory(
        pointHistoryId: number,
        payload: InvalidatePointHistoryPayload,
    ): Promise<InvalidatePointHistoryResult> {
        if (!pointHistoryId) {
            throw new Error("Point history ID is required");
        }

        const response = await super.apiRequest(
            `/api/v1/users/point-histories/${pointHistoryId}/invalidate`,
            HttpMethod.Post,
            payload,
            { noSpinner: true },
        );
        const data = (await response.json().catch(() => ({}))) as InvalidatePointHistoryResponse;

        if (!response.ok || !isInvalidatePointHistorySuccessResponse(data) || !data.success) {
            throw new Error(getResponseError(data) || "Failed to invalidate point history");
        }

        return data.data || null;
    }
}
